import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useUser } from "../../../../context/UserContext";
import { getDashboardStats } from "../../../../services/staffingService";
import {
  Briefcase, Shield, Users, Clock, ArrowRight, Loader2,
  AlertCircle, CheckCircle2, TrendingUp, ChevronRight,
  CalendarCheck, Info, Zap,
} from "lucide-react";
import { motion } from "framer-motion";
import DashboardSidebar from "../agencyDashboardSidebar";
import DashboardMap from "./DashboardMap";

/* ─── helpers ─────────────────────────────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

const JOB_STATUS_CFG = {
  open:     { label: "Open",     chip: "bg-red-50 text-red-700"           },
  disputed: { label: "Disputed", chip: "bg-amber-50 text-amber-700"       },
};

/* ─── KPI card ───────────────────────────────────────────────────── */
const KPICard = ({ icon: Icon, label, value, subtext, accent = "text-brand-pink", link, linkText, infoText }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-lg border border-border bg-card p-4"
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          {infoText && (
            <div className="group/info relative inline-block">
              <Info size={12} className="cursor-pointer text-muted-foreground transition-colors group-hover/info:text-brand-pink" />
              <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 opacity-0 transition-opacity group-hover/info:opacity-100">
                <div className="rounded-md bg-gray-950 px-3 py-2 text-[11px] text-white shadow-lg whitespace-nowrap">
                  {infoText}
                </div>
              </div>
            </div>
          )}
        </div>
        <p className="mt-2 text-2xl font-bold text-card-foreground">{value}</p>
        {subtext && <p className="mt-1 text-xs text-muted-foreground">{subtext}</p>}
        {link && linkText && (
          <Link to={link} className="mt-3 inline-flex text-xs font-medium text-brand-pink transition-colors hover:text-brand-pink/80">
            {linkText} →
          </Link>
        )}
      </div>
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
        accent === "text-brand-pink"   ? "bg-brand-pink/10" :
        accent === "text-blue-600"     ? "bg-blue-50"        :
        accent === "text-emerald-600"  ? "bg-emerald-50"     :
        accent === "text-amber-600"    ? "bg-amber-50"       :
        accent === "text-violet-600"   ? "bg-violet-50"      :
        "bg-muted"
      }`}>
        <Icon size={18} className={accent} />
      </div>
    </div>
  </motion.div>
);

/* ─── main component ─────────────────────────────────────────────── */
export default function AgencyHome() {
  const { providerCompanyId } = useParams();
  const { userInfo } = useUser();

  const [showSidebar, setShowSidebar] = useState(false);
  const [stats, setStats]             = useState(null);
  const [recentJobs, setRecentJobs]   = useState([]);
  const [officerLocations, setOfficerLocations]   = useState([]);
  const [customerLocations, setCustomerLocations] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (!providerCompanyId) return;
    async function fetchAll() {
      setLoading(true);
      try {
        const resp = await getDashboardStats(providerCompanyId);
        setStats(resp.data.counts);
        setRecentJobs(resp.data.recentJobs || []);
        setOfficerLocations(resp.data.officerLocations || []);
        setCustomerLocations(resp.data.customerLocations || []);
      } catch (err) {
        console.error("Dashboard stats error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [providerCompanyId]);

  const firstName = userInfo?.firstName || "there";

  // Officers-per-customer ratio
  const ratio =
    stats?.totalCustomers > 0 && stats?.totalOfficers > 0
      ? (stats.totalOfficers / stats.totalCustomers).toFixed(1)
      : "—";

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">

          {/* Page header */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl font-bold text-card-foreground">
              Good {greeting()}, {firstName} 👋
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Real-time overview of your agency
            </p>
          </motion.div>

          {/* KPI grid */}
          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <KPICard
                icon={Shield}
                label="Officers"
                value={stats?.totalOfficers ?? "—"}
                accent="text-brand-pink"
                link={`/provider-company/${providerCompanyId}/orm`}
                linkText="Manage officers"
              />
              <KPICard
                icon={Users}
                label="Customers"
                value={stats?.totalCustomers ?? "—"}
                accent="text-blue-600"
                link={`/provider-company/${providerCompanyId}/crm`}
                linkText="View customers"
              />
              <KPICard
                icon={Users}
                label="Officers / Customer"
                value={ratio}
                accent="text-violet-600"
                infoText={`On average ${ratio} officer${ratio !== '1.0' ? 's' : ''} per customer`}
                link={`/provider-company/${providerCompanyId}/dispatch`}
                linkText="Optimize in Dispatch"
              />
              <KPICard
                icon={AlertCircle}
                label="Needs Attention"
                value={(stats?.openJobs ?? 0) + (stats?.disputedJobs ?? 0)}
                accent="text-amber-600"
                subtext={`${stats?.openJobs ?? 0} open · ${stats?.disputedJobs ?? 0} disputed`}
                link={`/provider-company/${providerCompanyId}/dispatch`}
                linkText="Go to Dispatch"
              />
            </div>
          )}

          {/* 2-column: Active jobs + Map */}
          <div className="flex gap-6 items-stretch" style={{ height: 380 }}>

            {/* Left 60%: Active jobs table */}
            <div className="flex-1 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
              <div className="flex items-center justify-between border-b border-border px-4 py-3 flex-shrink-0">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                  Active Jobs
                </p>
                <Link
                  to={`/provider-company/${providerCompanyId}/dispatch`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-brand-pink transition-colors hover:text-brand-pink/80"
                >
                  Open Dispatch <ArrowRight size={12} />
                </Link>
              </div>

              <div className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={16} className="animate-spin text-muted-foreground" />
                  </div>
                ) : recentJobs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle2 size={24} className="mb-2 text-emerald-400" />
                    <p className="text-sm font-medium text-card-foreground">All caught up!</p>
                    <p className="text-xs text-muted-foreground mt-0.5">No open or disputed jobs</p>
                  </div>
                ) : (
                  <div className="overflow-auto h-full">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10">
                        <tr className="border-b border-border bg-muted/50">
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Customer</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Service</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Status</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Created</th>
                          <th className="px-4 py-2.5" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {recentJobs.map((job, i) => {
                          const cfg = JOB_STATUS_CFG[job.status] || JOB_STATUS_CFG.open;
                          const customerName =
                            job.customer_company_local_id?.companyFriendlyName ||
                            job.customer_company_local_id?.companyLegalName ||
                            job.customer_company_id?.companyFriendlyName ||
                            job.customer_company_id?.companyName ||
                            "Unknown";

                          return (
                            <motion.tr
                              key={job._id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: i * 0.03 }}
                              className="cursor-pointer transition-colors hover:bg-muted/30"
                            >
                              <td className="px-4 py-3 text-xs font-medium text-card-foreground">{customerName}</td>
                              <td className="px-4 py-3 text-xs text-muted-foreground">{job.serviceType || "—"}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${cfg.chip}`}>
                                  {cfg.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(job.createdAt)}</td>
                              <td className="px-4 py-3">
                                <Link
                                  to={`/provider-company/${providerCompanyId}/dispatch/job/${job._id}`}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-brand-pink transition-colors hover:text-brand-pink/80"
                                >
                                  View <ChevronRight size={12} />
                                </Link>
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Right 40%: Map */}
            <div className="w-[40%] flex-shrink-0 rounded-lg overflow-hidden border border-border">
              {loading ? (
                <div className="flex h-full w-full items-center justify-center bg-muted rounded-lg">
                  <Loader2 size={20} className="animate-spin text-muted-foreground" />
                </div>
              ) : (
                <DashboardMap
                  officers={officerLocations}
                  customers={customerLocations}
                />
              )}
            </div>
          </div>

          {/* Pending offers callout */}
          {!loading && stats?.pendingOffers > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4"
            >
              <Clock size={20} className="flex-shrink-0 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">
                  {stats.pendingOffers} pending offer{stats.pendingOffers !== 1 ? "s" : ""} awaiting response
                </p>
                <p className="text-xs text-amber-700">
                  Officers have been sent offers — check for acceptances.
                </p>
              </div>
              <Link
                to={`/provider-company/${providerCompanyId}/dispatch`}
                className="flex-shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700 no-underline"
              >
                Review in Dispatch
              </Link>
            </motion.div>
          )}

          {/* Quick Access — bottom */}
          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Quick Access
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { to: `/provider-company/${providerCompanyId}/dispatch`, icon: CalendarCheck, label: "Dispatch",    sub: "Jobs, shifts & assign"  },
                { to: `/provider-company/${providerCompanyId}/orm`,      icon: Shield,         label: "Officers",    sub: "Roster management"      },
                { to: `/provider-company/${providerCompanyId}/crm`,      icon: Users,          label: "Customers",   sub: "Customer accounts"      },
                { to: `/provider-company/${providerCompanyId}/team`,     icon: TrendingUp,     label: "Team",        sub: "Managers & roles"       },
              ].map(({ to, icon: Icon, label, sub }) => (
                <Link
                  key={to}
                  to={to}
                  className="group flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-all hover:border-brand-pink/30 hover:shadow-sm no-underline"
                >
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-pink/10">
                    <Icon size={16} className="text-brand-pink" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-card-foreground">{label}</p>
                    <p className="text-[10px] text-muted-foreground">{sub}</p>
                  </div>
                  <ArrowRight size={14} className="flex-shrink-0 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}


